import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import Swal from 'sweetalert2';
import logo from '../logo.png';

const Login = () => {
    const userRef = useRef();
    const [username, setUserDNI] = useState('');
    const [name, setName] = useState('');
    const [userId, setUserId] = useState(null);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [isHIFT, setIsHIFT] = useState(false);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [selectedExercises, setSelectedExercises] = useState([]);
    const [hasExpiredQuota, setHasExpiredQuota] = useState(false);
    const [lastPaymentDate, setLastPaymentDate] = useState(null);
    const [quotaValidUntil, setQuotaValidUntil] = useState(null);

    useEffect(() => {
        userRef.current.focus();
    }, []);

    const checkQuotaExpired = (membershipExpiresAt, lastPayment) => {
        if (!membershipExpiresAt && !lastPayment) return true;

        const validUntilDate = membershipExpiresAt
            ? new Date(`${String(membershipExpiresAt).slice(0, 10)}T00:00:00`)
            : new Date(lastPayment);
        const today = new Date();

        if (!membershipExpiresAt) {
            validUntilDate.setDate(validUntilDate.getDate() + 30);
        }

        // Resetear las horas para comparar solo fechas
        validUntilDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        // Guardar la fecha hasta cuando está válida la cuota
        setQuotaValidUntil(validUntilDate);

        // La fecha de vencimiento es el primer día sin cobertura.
        return validUntilDate <= today;
    };

    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();
        setError(null);
        setSuccessMessage('');
        setHasExpiredQuota(false);
        setQuotaValidUntil(null);

        try {
            const response = await axios.get(`https://wellness.nextriking.com/api/users/email/${username}`);

            if (response.data.error === false && response.data.body.length > 0) {
                const user = response.data.body[0];
                setUserId(user.id);
                setName(user.name);
                setLastPaymentDate(user.lastPayment);

                const isExpired = checkQuotaExpired(user.membership_expires_at, user.lastPayment);
                setHasExpiredQuota(isExpired);

                setSuccessMessage("Usuario encontrado. Completa el formulario.");
                setShowModal(true);
            } else {
                setError('Usuario no encontrado.');
            }
        } catch (error) {
            setError('Error al iniciar sesión. Inténtalo de nuevo más tarde.');
            console.error('Error en inicio de sesión:', error);
        }
    }, [username]);

    const handleFormSubmit = useCallback(async (event) => {
        event.preventDefault();

        try {
            const response = await enviarDatosSesion(userId, selectedWeek, selectedExercises, isHIFT);

            if (response) {
                Swal.fire({
                    title: "¡Éxito!",
                    text: response.message,
                    icon: "success",
                    confirmButtonText: "OK",
                    timer: 4000,
                    timerProgressBar: true,
                    showConfirmButton: true,
                    willClose: () => {
                        console.log('Diálogo cerrándose');
                    }
                }).then((result) => {
                    console.log('Resultado:', result);

                    setShowModal(false);
                    setSuccessMessage("");
                    setName("")
                    setUserId('')
                    setUserDNI('')
                    setIsHIFT(false)
                    setSelectedWeek(null)
                    setSelectedExercises([])
                    setHasExpiredQuota(false)
                    setLastPaymentDate(null)
                    setQuotaValidUntil(null)
                });
            }
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: "Hubo un problema, reintenta luego.",
                icon: "error",
                confirmButtonText: "Intentar de nuevo"
            });
        }
    }, [userId, selectedWeek, selectedExercises, isHIFT]);

    const enviarDatosSesion = async (userId, semanasEntrenamiento, todayRoutine, isHIFT) => {
        try {
            const payload = isHIFT
                ? { userId, establishmentId: 1, trainingWeek: "", todayRoutine: "Sala HIFT" }
                : { userId, establishmentId: 1, trainingWeek: semanasEntrenamiento, todayRoutine: todayRoutine.length > 0 ? todayRoutine[0] : "Sala HIFT" };

            console.log("Enviando payload:", payload);

            const response = await axios.post(
                "https://wellness.nextriking.com/api/sessions/in",
                payload,
                { headers: { "Content-Type": "application/json" } }
            );

            console.log("Respuesta del servidor:", response.data);
            return response.data;
        } catch (error) {
            console.error("Error al enviar los datos:", error.response?.data || error.message);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Sin registro';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const exerciseOptions = [
        "Básicos", "Brazos", "Cadera Dominante", "CORE", "Empuje",
        "FullBody", "Piernas", "PushPull", "Rodilla Dominante", "Tracción"
    ];

    return (
        <div className="container mt-5">
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="card p-4 text-center" style={{ width: "320px" }}>
                    <img src={logo} alt="Wellness Logo" className="mb-3 mx-auto d-block" style={{ width: "100px" }} />
                    <h3 className="mb-3">Bienvenido a Wellness</h3>
                    <form onSubmit={handleSubmit}>
                        <input
                            type="text"
                            ref={userRef}
                            value={username}
                            onChange={(e) => setUserDNI(e.target.value)}
                            placeholder="Ingresa tu DNI"
                            className="form-control mb-2"
                        />
                        <button type="submit" className="btn btn-primary w-100">Buscar usuario</button>
                        {error && <p className="text-danger mt-2">{error}</p>}
                        {successMessage && <p className="text-success mt-2">{successMessage}</p>}
                    </form>
                </div>
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{name}, ¿Qué vas a hacer hoy?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Mensaje de cuota vencida */}
                    {hasExpiredQuota && (
                        <Alert variant="danger" className="mb-3">
                            <Alert.Heading className="d-flex align-items-center">
                                <span style={{ fontSize: '1.5rem', marginRight: '8px' }}>⚠️</span>
                                Cuota Vencida
                            </Alert.Heading>
                            <p className="mb-1">
                                <strong>Tu cuota de pago está vencida.</strong>
                            </p>
                            <p className="mb-1">
                                Último pago: <strong>{formatDate(lastPaymentDate)}</strong>
                            </p>
                            <p className="mb-2">
                                Vencimiento: <strong>{formatDate(quotaValidUntil)}</strong>
                            </p>
                            <hr />
                            <p className="mb-0" style={{ fontSize: '0.95rem' }}>
                                Por favor, acércate al mostrador para regularizar tu situación antes de entrenar.
                            </p>
                        </Alert>
                    )}

                    <form onSubmit={handleFormSubmit}>
                        <div className="mb-3 form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="isHIFT"
                                checked={isHIFT}
                                onChange={(e) => setIsHIFT(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="isHIFT">¿Vas a entrenar en Sala HIFT?</label>
                        </div>

                        {!isHIFT && (
                            <>
                                <div className="mb-3">
                                    <label className="form-label">Semana de entrenamiento</label>
                                    <select
                                        className="form-control"
                                        value={selectedWeek || ""}
                                        onChange={(e) => setSelectedWeek(Number(e.target.value))}
                                    >
                                        <option value="">Selecciona una semana</option>
                                        {Array.from({ length: 16 }, (_, i) => i + 1).map((week) => (
                                            <option key={week} value={week}>
                                                Semana {week}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Ejercicios a realizar</label>
                                    {exerciseOptions.map((exercise) => (
                                        <div key={exercise} className="form-check">
                                            <input
                                                type="radio"
                                                className="form-check-input"
                                                id={exercise}
                                                name="exerciseSelection"
                                                value={exercise}
                                                checked={selectedExercises.includes(exercise)}
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    setSelectedExercises([value]);
                                                }}
                                            />
                                            <label className="form-check-label" htmlFor={exercise}>{exercise}</label>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        <Button type="submit" variant="success">Enviar</Button>
                    </form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default Login;
